import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { asyncScheduler, Observable, scheduled } from 'rxjs';

import { WizardStep } from '../../../../../shared/controls/wizard-control/wizard-step.interface';
import { ToastService } from '../../../../../shared/toast/toast.service';
import { ErrorService } from '../../../../../shared/services/error-service';

import { PremiumDataRangeMappingInput, PremiumDataService, WorkbookService } from '../../../../../../service/proxy';

@Component({
    selector: 'premium-data-row-selection',
    templateUrl: './premium-data-row-selection.component.html',
    styleUrls: ['./premium-data-row-selection.component.scss'],

    imports: [
        CommonModule,
        FormsModule
    ],

    providers: [
        PremiumDataService,
        WorkbookService
    ]
})
export class PremiumDataRowSelectionComponent implements WizardStep {
    @ViewChild('form')
    form!: NgForm;

    public batchId: number = 0;

    public firstTenRows: any[] = [];
    public lastTenRows: any[] = [];
    public loading: boolean = false;

    public model: PremiumDataRangeMappingInput = new PremiumDataRangeMappingInput();

    constructor(
        private route: ActivatedRoute,
        private toastService: ToastService,
        private errorService: ErrorService,
        private premiumDataService: PremiumDataService,
        private workbookService: WorkbookService
    ) {}

    onActivate(): void {
        this.route.paramMap
            .subscribe(params => {
                this.batchId = Number(params.get('id'));

                if (this.batchId > 0) {
                    // Always reload the saved range values from the database
                    this.getImportRowMapping();

                    // Only fetch the file preview on first activation.
                    // On back-navigation the rows are already in memory — no need to hit the server again.
                    if (this.firstTenRows.length === 0) {
                        this.getBatch();
                    }
                }
            }
        );
    }

    getBatch(): void {
        this.premiumDataService.getPremiumDataBatchById(this.batchId)
            .subscribe((result) => {
                this.getWorksheetSample(`${result.filePath}//${result.storedName}`);
            }
        );
    }

    getImportRowMapping(): void {
        this.premiumDataService.getPremiumDataImportRangeMapping(this.batchId)
            .subscribe((result) => {
                if (result) {
                    this.model = new PremiumDataRangeMappingInput({
                        headerIndex: result.headerIndex || 1,
                        startIndex: result.startIndex || 2,
                        endIndex: result.endIndex,
                    });
                }
            }
        );
    }

    getWorksheetSample(filePath: string): void {
        this.loading = true;
        this.workbookService.getSampleBySheetIndex(filePath, 0)
            .subscribe({
                next: (result) => {
                    this.firstTenRows = result.firstTenRows || [];
                    this.lastTenRows = result.lastTenRows || [];

                    //Set endIndex to the last row number
                    const lastRow = this.lastTenRows[this.lastTenRows.length - 1];

                    if (lastRow?.rowNumber) {
                        this.model.endIndex = lastRow.rowNumber;
                    }
                },
                error: (error) => {
                    if (error.status === 400) {
                        this.toastService.show('Bad request. Please try again.', 'info');
                    }
                    else if (error.status === 404) {
                        const errorBody = error.response ? JSON.parse(error.response) : null;
                        if (errorBody?.code === 'FILE_NOT_FOUND') {
                            this.toastService.show('Excel file not found. Delete this import batch and upload again.', 'warning');
                        } else {
                            this.toastService.show('File not found. Please try again.', 'info');
                        }
                    }
                    else {
                        this.errorService.handleError(error);
                    }
                    this.loading = false;
                },
                complete: () => {
                    this.loading = false;
                }
            }
        );
    }

    isValid(): boolean {
        if (this.form.valid) {
            return true;
        }

        return false;
    }

    onNext(): Observable<boolean> {
        return new Observable<boolean>(observer => {
            this.premiumDataService.createPremiumDataImportRangeMapping(this.batchId, this.model)
                .subscribe({
                    next: () => {
                        this.toastService.show('Premium data row mapping saved successfully', 'Success');

                        // Range changed — tell import step to re-run, same as column mapping does
                        const mappingsChangedKey = `premium_data_mappings_changed_${this.batchId}`;
                        sessionStorage.setItem(mappingsChangedKey, 'true');

                        observer.next(true);
                        observer.complete();
                    },
                    error: (error: any) => {
                        if (error.status === 400) {
                            this.toastService.show('Bad request. Please try again.', 'Info');
                        }
                        else {
                            this.errorService.handleError(error);
                        }

                        observer.next(false);
                        observer.complete();
                    },
                }
            );
        });
    }

    onPrevious(): Observable<boolean> {
        return scheduled([true], asyncScheduler);
    }
}